// The ring a person hears while a call waits in this browser, made here rather than from a sound file: the North
// American two-tone, two seconds on and four off. A browser keeps sound off until the page is touched, so the sound is
// unlocked on the click that turns answering on and on the first tap, click or key of every visit; until then the banner
// shows without a ring.
let shared: AudioContext | null = null;

function context(): AudioContext | null {
  if (shared && shared.state !== 'closed') return shared;
  try {
    shared = new AudioContext();
  } catch {
    shared = null;
  }
  return shared;
}

/** Unlock the sound now; call it from a click or key handler. */
export function unlockRing() {
  void context()?.resume().catch(() => { /* still locked */ });
}

/** Unlock the sound on the first touch or key of this visit. Returns the cleanup. */
export function unlockRingOnTouch(): () => void {
  // pointerup and keydown count as the person's own gesture on a mouse and on a touch screen (pointerdown does not on
  // touch); the listeners stay until the sound is really running
  const once = () => {
    const ctx = context();
    if (!ctx) { off(); return; }
    void ctx.resume().then(() => { if (ctx.state === 'running') off(); }).catch(() => { /* still locked */ });
  };
  const off = () => {
    window.removeEventListener('pointerup', once, true);
    window.removeEventListener('keydown', once, true);
  };
  window.addEventListener('pointerup', once, true);
  window.addEventListener('keydown', once, true);
  return off;
}

export function startRing(): () => void {
  const ctx = context();
  if (!ctx) return () => {};
  void ctx.resume().catch(() => { /* silent until the page is touched */ });
  const gain = ctx.createGain();
  gain.gain.value = 0;
  gain.connect(ctx.destination);
  const tones = [440, 480].map((f) => {
    const o = ctx.createOscillator();
    o.frequency.value = f;
    o.connect(gain);
    o.start();
    return o;
  });
  const burst = () => {
    const t = ctx.currentTime;
    gain.gain.setValueAtTime(0.06, t);
    gain.gain.setValueAtTime(0, t + 2);
  };
  burst();
  const timer = window.setInterval(burst, 6000);
  return () => {
    window.clearInterval(timer);
    gain.gain.cancelScheduledValues(0);
    gain.gain.value = 0;
    for (const o of tones) { try { o.stop(); } catch { /* already stopped */ } o.disconnect(); }
    gain.disconnect();
  };
}
